import { Browser } from "@capacitor/browser";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { Platform } from "./platform";

function isInternalUrl(url: string): boolean {
  if (url.startsWith("//")) {
    return false;
  }
  if (url.startsWith("/") || url.startsWith("#")) {
    return true;
  }
  try {
    return new URL(url).origin === globalThis.location.origin;
  } catch {
    return false;
  }
}

/**
 * Returns a function that opens a URL in the appropriate context based on platform and URL type.
 *
 * On native mobile:
 * - Internal URLs (same origin) navigate via React Router to preserve the auth session and SPA state
 * - External URLs open in an in-app browser (SFSafariViewController / Chrome Custom Tabs)
 *
 * On web, opens a new tab.
 */
export function useOpenUrl() {
  const history = useHistory();

  return useCallback(
    async (url: string): Promise<void> => {
      if (Platform.isMobile) {
        if (isInternalUrl(url)) {
          const { pathname, search, hash } = new URL(url, globalThis.location.origin);
          history.push(`${pathname}${search}${hash}`);
        } else {
          await Browser.open({ url });
        }
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [history]
  );
}

/**
 * Opens a URL that needs to be fetched asynchronously, without tripping Safari's
 * popup blocker. Safari requires `window.open` to run synchronously inside a
 * user-gesture handler; an awaited fetch before `window.open` breaks that chain
 * and the popup is silently dropped.
 *
 * On web, this opens an `about:blank` placeholder tab synchronously when called,
 * then redirects it to the resolved URL. On Capacitor mobile (where window.open
 * is unsupported and Capacitor's Browser.open is not gesture-restricted), it
 * just delegates to `useOpenUrl`. If the placeholder is blocked outright on web,
 * falls back to a same-tab navigation — opening another popup post-await would
 * hit the same blocker and silently fail.
 *
 * The returned function MUST be invoked synchronously inside the click handler;
 * any `await` before the call will reintroduce the popup-blocker bug.
 */
export function useOpenAsyncUrl() {
  const openUrl = useOpenUrl();

  return useCallback(
    async (getUrl: () => Promise<string>): Promise<void> => {
      const placeholderTab = Platform.isMobile ? null : globalThis.open("about:blank", "_blank");

      try {
        const url = await getUrl();

        if (placeholderTab && !placeholderTab.closed) {
          placeholderTab.opener = null;
          placeholderTab.location.href = url;
        } else if (Platform.isMobile) {
          await openUrl(url);
        } else {
          globalThis.location.assign(url);
        }
      } catch (error) {
        placeholderTab?.close();
        throw error;
      }
    },
    [openUrl]
  );
}
